import { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import { fetchPublicNote, fetchNoteByURLFriendlyName, fetchNotes } from "../store/slices/notesSlice";
import { Note } from "../types";

export const useNote = (identifier: string, isURLFriendly: boolean = false) => {
  const dispatch = useAppDispatch();
  const { items: notes, loading: storeLoading } = useAppSelector((state) => state.notes);
  const currentUser = useAppSelector((state) => state.user.profile);
  const [note, setNote] = useState<Note | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (identifier) {
      const fetchNote = async () => {
        setLoading(true);
        setError(null);
        try {
          if (isURLFriendly) {
            // For URL-friendly names, always use the dedicated API
            const result = await dispatch(fetchNoteByURLFriendlyName(identifier));
            if (fetchNoteByURLFriendlyName.fulfilled.match(result)) {
              setNote(result.payload as Note);
            } else {
              setError("Note not found or access denied");
            }
          } else {
            // For ID-based access, try public API first, then fallback to authenticated
            const publicResult = await dispatch(fetchPublicNote(identifier));
            if (fetchPublicNote.fulfilled.match(publicResult)) {
              setNote(publicResult.payload as Note);
            } else {
              // If public API fails, try authenticated API if user is logged in
              if (currentUser) {
                await dispatch(fetchNotes());
                // The note should now be in the store, but we'll let the component handle it
                // since we can't access the updated store here immediately
              } else {
                setError("Note not found or access denied");
              }
            }
          }
        } catch (err) {
          setError(err instanceof Error ? err.message : "Unknown error");
        } finally {
          setLoading(false);
        }
      };

      fetchNote();
    }
  }, [identifier, isURLFriendly, dispatch, currentUser]);

  // For ID-based access, also check the store for the note after fetchNotes
  useEffect(() => {
    if (!isURLFriendly && !note && !loading && !error && notes.length > 0) {
      const foundNote = notes.find((n) => n.id === identifier);
      if (foundNote) {
        setNote(foundNote);
      }
    }
  }, [notes, identifier, isURLFriendly, note, loading, error]);

  return { note, loading: loading || storeLoading, error };
};
